import React, { useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { marketingApiService } from '../../../../marketing-v2/services/marketingApiService'
import { analyseSmsExpanded, previewSubstitute, ownBookingLink } from '../../../../marketing-v2/features/sms-composer/smsUtils'
import { hapticTick, hapticSuccess, hapticError } from '../../../../../_utils/haptics'
import { textDraft } from '../makeThings'
import { useBrand, useRecall } from '../useBeu'
import { Tick } from './icons'
import { CAMPAIGNS_PATH } from './EmailWidget'
import { getUsageState } from '../../../../pricing/usageStore'
import { formatUnitPence } from '../../../../pricing/money'
import { useSaver } from '../saveBus'

const Bubble = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 12a8 8 0 01-11.6 7.1L4 20l1-4.1A8 8 0 1120 12z" />
  </svg>
)

// A marketing text Bailey wrote, shown as the client will get it: the salon's
// links filled in, the opt-out line Beu adds, and how many texts (and
// credits) that is per client. Saving makes a draft in Campaigns and nothing
// more: the salon picks who gets it and sends it from there. Bailey used to
// paste texts as a quote, with nothing to tap (QA 2026-10-02).
const TextWidget = ({ block, id, go, latest = false }) => {
  const brand = useBrand()
  const dispatch = useDispatch()
  const [memo, setMemo] = useRecall(id)
  const [busy, setBusy] = useState(false)
  // Set at once on a tap: busy is state, so a quick double tap saw it false
  // twice and made two drafts
  const busyRef = useRef(false)
  const [error, setError] = useState(null)
  const campaignId = (memo && memo.campaignId) || block.draftId || null
  const draft = useMemo(() => textDraft(block, { links: brand.links }), [block, brand.links])
  const est = useMemo(() => analyseSmsExpanded(draft.message, brand.salonName), [draft.message, brand.salonName])
  // The salon's own booking page in the preview, not the composer's
  // placeholder (the send fills in the real one either way)
  const booking = ownBookingLink(brand.links)
  const shown = previewSubstitute(booking ? draft.message.split('{{BookingLink}}').join(booking) : draft.message, brand.salonName) || draft.message

  const save = async () => {
    // Saved already: the same draft, never a second one
    if (campaignId) return campaignId
    if (busyRef.current) return null
    busyRef.current = true
    setBusy(true)
    setError(null)
    hapticTick()
    try {
      const res = await marketingApiService.createCampaign(dispatch, draft)
      const cid = res && (res._id || res.id || res.campaignId)
      if (!cid) throw new Error('no id')
      setMemo({ campaignId: cid })
      hapticSuccess()
      busyRef.current = false
      setBusy(false)
      return cid
    } catch (e) {
      hapticError()
      setError('The draft couldn’t be saved just now. Nothing was sent.')
    }
    busyRef.current = false
    setBusy(false)
    return null
  }
  const open = (cid = campaignId) => { hapticTick(); go(CAMPAIGNS_PATH, cid ? { openCampaignId: cid } : { compose: 'sms' }) }
  const saveAndOpen = async () => { const cid = await save(); if (cid) open(cid) }
  const texts = est.segments || 1
  // What a text costs, from Plan & usage when it's loaded; no guess otherwise.
  const known = getUsageState().usage
  const textPence = known && known.prices && known.prices.textPence != null ? Number(known.prices.textPence) : null

  // "Yes" in the chat saves the draft (never sends)
  useSaver(id, latest && !campaignId && !!draft.message, async () => {
    const cid = await save()
    return cid ? `the text "${block.title || 'marketing text'}" saved as a draft in Campaigns (not sent)` : null
  })

  return (
    <div className={`bw-card is-text${campaignId ? ' is-done' : ''}`} role="group" aria-label={block.title || 'Text'}>
      <div className="bw-head">
        <span className="bw-kind"><Bubble />Text</span>
        {campaignId && <span className="bw-status is-done"><Tick size={12} />Draft in Campaigns</span>}
      </div>
      {block.title && <p className="bw-title">{block.title}</p>}
      <p className="bw-sms">{shown}</p>
      <p className="bw-line">{texts === 1 ? '1 text' : `${texts} texts`} per client{textPence != null ? ` · ${formatUnitPence(texts * textPence)} each` : ''}</p>
      {error && <p className="bw-need" role="alert">{error}</p>}
      <div className="bw-actions">
        {!campaignId && (
          <button type="button" className="bw-btn is-secondary" onClick={save} disabled={busy}>{busy ? 'Saving' : 'Save as a draft'}</button>
        )}
        <button type="button" className="bw-btn is-primary" onClick={() => (campaignId ? open() : saveAndOpen())} disabled={busy}>
          {campaignId ? 'Open in Campaigns' : 'Save and open'}
        </button>
      </div>
      {!campaignId && <p className="bw-foot">Nothing goes out until you send it from Campaigns, where you choose who gets it.</p>}
    </div>
  )
}

export default TextWidget
